import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Order,
    fetchOrder,
    selectOrder,
    selectError,
    selectIsLoading,
    selectPaymentGatewayRedirect,
    OrderStatus,
    orderNamePrefix,
} from "../features/payment/paymentSlice";
import { useDispatch, useSelector } from "react-redux";
import LoadScreen from "../components/LoadScreen";
import ErrorScreen from "../components/ErrorScreen";
import { selectIsAuthenticated, selectTokens } from "../features/auth/authSlice";
import { Checkout } from "../components/Checkout";
import RedirectScreen from "../components/RedirectScreen";
import { Container, LoadingOverlay, Skeleton } from "@mantine/core";
import { useParams } from "react-router-dom";
import { AppDispatch } from "../app/store";
import { isIOSDevice, logBrowserInfo } from "../util/iosDetection";
import { logCrossSubdomainAuthStatus } from "../util/crossSubdomainAuth";
import { runCookieTests } from "../util/cookieTest";


// OrderPage renders a order in all of its possible state together with buttons to pay it if applicable
const OrderPage: React.FC<{}> = () => {
    const { id } = useParams<{ id: string }>();
    const order = useSelector(selectOrder);
    const redirectURL = useSelector(selectPaymentGatewayRedirect);
    const isLoading = useSelector(selectIsLoading);
    const error = useSelector(selectError);
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const tokensDebug = useSelector(selectTokens);
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();
    
    // iOS detection for debugging and special handling
    const [isIOS, setIsIOS] = useState(false);
    const [loadingTimeout, setLoadingTimeout] = useState(false);
    
    useEffect(() => {
        // Detect iOS devices and log browser info for debugging
        const isIOS = isIOSDevice();
        setIsIOS(isIOS);
        logBrowserInfo();
        
        // Debug authentication flow - only in test or staging environments
        if (window.location.hostname.toLowerCase().includes('test') || window.location.hostname.toLowerCase().includes('staging')) {
            console.log('=== AUTHENTICATION DEBUG ===');
            console.log('Current domain:', window.location.hostname);
            console.log('Current URL (sanitized):', window.location.origin + window.location.pathname + '[QUERY_PARAMS_REDACTED]');
            console.log('Keycloak realm:', 'VSETH');
            console.log('Is authenticated:', isAuthenticated);
            console.log('Is loading:', isLoading);
            console.log('Error:', error);
            
            // Check for subdomain cookie sharing issue
            console.log('=== SUBDOMAIN DEBUG ===');
            console.log('All cookies (sanitized):', document.cookie.split(';').map(c => {
                const [name, value] = c.trim().split('=');
                if (!value) return c.trim();
                
                // Hide sensitive cookie values
                const sensitiveNames = ['token', 'idtoken', 'kc-access', 'kc-refresh', 'session', 'auth'];
                if (sensitiveNames.some(sensitive => name.toLowerCase().includes(sensitive))) {
                    return `${name}=[REDACTED]`;
                }
                
                // Truncate long values
                if (value.length > 20) {
                    return `${name}=${value.substring(0, 20)}...[TRUNCATED]`;
                }
                
                return c.trim();
            }));
            console.log('Keycloak cookies (sanitized):', document.cookie.split(';').filter(c => c.includes('keycloak') || c.includes('kc')).map(c => {
                const [name, value] = c.trim().split('=');
                return `${name}=[REDACTED]`;
            }));
            
            // Log cross-subdomain authentication status
            logCrossSubdomainAuthStatus();
            
            // Run cookie sharing tests
            runCookieTests();
        }
        
        if (isIOS) {
            if (window.location.hostname.toLowerCase().includes('test') || window.location.hostname.toLowerCase().includes('staging')) {
                console.log('iOS device detected');
                console.log('localStorage available:', typeof Storage !== 'undefined' && typeof localStorage !== 'undefined');
                console.log('sessionStorage available:', typeof Storage !== 'undefined' && typeof sessionStorage !== 'undefined');
                console.log('Cookies available:', typeof document !== 'undefined' && typeof document.cookie !== 'undefined');
            }
        }
        
        // iOS-specific timeout to prevent infinite loading
        if (isIOS) {
            const timeoutId = setTimeout(() => {
                setLoadingTimeout(true);
                console.warn("iOS loading timeout reached - this may indicate an authentication issue");
            }, 30000); // 30 second timeout for iOS
            
            return () => clearTimeout(timeoutId);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated || isLoading || id === undefined) {
            return;
        } else {
            if (window.location.hostname.toLowerCase().includes('test') || window.location.hostname.toLowerCase().includes('staging')) {
                console.log('=== FETCHING ORDER ===');
                console.log('Order ID:', id);
                console.log('Is authenticated:', isAuthenticated);
                console.log('Is loading:', isLoading);
            }
            dispatch(fetchOrder(orderNamePrefix + id));
        }
    }, [dispatch, id, isAuthenticated]);

    // Refresh page when user returns after payment (replicates F5 behavior)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && isAuthenticated && id && !isLoading) {
                if (window.location.hostname.toLowerCase().includes('test') || window.location.hostname.toLowerCase().includes('staging')) {
                    console.log('=== PAGE VISIBLE - REFRESHING PAGE ===');
                    console.log('Page became visible, refreshing page (like F5)');
                }
                // Small delay to ensure the page is fully visible
                setTimeout(() => {
                    window.location.reload();
                }, 100);
            }
        };

        const handleFocus = () => {
            if (isAuthenticated && id && !isLoading) {
                if (window.location.hostname.toLowerCase().includes('test') || window.location.hostname.toLowerCase().includes('staging')) {
                    console.log('=== WINDOW FOCUS - REFRESHING PAGE ===');
                    console.log('Window gained focus, refreshing page (like F5)');
                }
                // Small delay to ensure the window is fully focused
                setTimeout(() => {
                    window.location.reload();
                }, 100);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [dispatch, id, isAuthenticated, isLoading]);

    // Redirect when gateway url is ready
    if (redirectURL) {
        if (window.location.hostname.toLowerCase().includes('test') || window.location.hostname.toLowerCase().includes('staging')) {
            console.log('=== PAYMENT GATEWAY REDIRECT ===');
            console.log('Redirecting to payment gateway:', redirectURL);
            console.log('Current order status:', order?.status);
            console.log('Order data:', order);
        }
        return <RedirectScreen to={redirectURL} />;
    }

    if (isLoading) {
        if (window.location.hostname.toLowerCase().includes('test') || window.location.hostname.toLowerCase().includes('staging')) {
            console.log('=== ORDER LOADING ===');
            console.log('Loading order data...');
        }
        // Show timeout error for iOS if loading takes too long
        if (isIOS && loadingTimeout) {
            return (
                <ErrorScreen 
                    error={new Error("Loading timeout on iOS - this may be due to authentication issues. Please try refreshing the page or clearing your browser cache.")} 
                />
            );
        }
        
        return (
            <LoadScreen
                header={t("OrderPage.wait")}
                body={isIOS ? 
                    t("OrderPage.orderPreparingIOS") || t("OrderPage.orderPreparing") + " (iOS detected)" :
                    t("OrderPage.orderPreparing")
                }
            />
        );
    }

    if (!isAuthenticated) {
        return <LoadingOverlay visible={!isAuthenticated} overlayBlur={2}>
            <Skeleton animate={false} height={8} mt={6} radius="xl" />
            <Skeleton animate={false} height={8} mt={6} radius="xl" />
            <Skeleton animate={false} height={8} mt={6} radius="xl" />
        </LoadingOverlay>;
    }

    if (error || !order) {
        if (window.location.hostname.toLowerCase().includes('test') || window.location.hostname.toLowerCase().includes('staging')) {
            console.log('=== ORDER ERROR ===');
            console.log('Error loading order:', error);
            console.log('Order data:', order);
        }
        // Show iOS-specific error message if authentication fails on iOS
        if (isIOS && error && error.message?.includes('auth')) {
            return <ErrorScreen error={new Error(t("OrderPage.iosAuthError"))} />;
        }
        return <ErrorScreen error={error} />;
    }

    switch (order.status) {
        case OrderStatus.OPEN:
            if (window.location.hostname.toLowerCase().includes('test') || window.location.hostname.toLowerCase().includes('staging')) {
                console.log('=== ORDER STATUS: OPEN ===');
                console.log('Order is open, showing checkout form');
            }
            return <OrderOpen order={order} />;
        case OrderStatus.PAID:
            if (window.location.hostname.toLowerCase().includes('test') || window.location.hostname.toLowerCase().includes('staging')) {
                console.log('=== ORDER STATUS: PAID ===');
                console.log('Order is paid, redirecting to client app');
                console.log('Callback URL:', order.callbackUrl);
                console.log('Order number:', order.number);
            }
            return (
                <RedirectScreen
                    to={order.callbackUrl}
                    explanation={t("OrderPage.paidExplanation", {
                        reference: order.number,
                    })}
                />
            );
        case OrderStatus.EXPIRED:
            if (window.location.hostname.toLowerCase().includes('test') || window.location.hostname.toLowerCase().includes('staging')) {
                console.log('=== ORDER STATUS: EXPIRED ===');
                console.log('Order has expired');
            }
            return <OrderExpired order={order} />;
        default:
            if (window.location.hostname.toLowerCase().includes('test') || window.location.hostname.toLowerCase().includes('staging')) {
                console.log('=== ORDER STATUS: UNKNOWN ===');
                console.log('Unknown order status:', order.status);
            }
            console.log("error", "Unknown order status" + order.status);
            return <ErrorScreen error={error} />;
    }
};

interface PropsWithOrder {
    order: Order;
}

const OrderOpen: React.FC<PropsWithOrder> = ({ order }) => {
    return (
        <Container>
            <Checkout order={order} />
        </Container>
    );
};

const OrderExpired: React.FC<PropsWithOrder> = ({ order }) => {
    const { t } = useTranslation();

    return (
        <div
            className="d-flex flex-column justify-content-center text-center"
            style={{ height: "80vh" }}
        >
            <h1>{t("OrderPage.expired")}</h1>
            <p>{t("OrderPage.orderExpired", { reference: order.number })}</p>
        </div>
    );
};

export default OrderPage;
